import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContactMessages,
  updateContactReadStatus,
  markContactAsUnread,
} from "./contactMessages.api";
import type {
  ContactMessagesParams,
  ContactMessagesResponse,
} from "../types/contact.types";

export const useGetContactMessages = (params: ContactMessagesParams = {}) => {
  return useQuery({
    queryKey: ["contact-messages", params.skip, params.limit],
    queryFn: () => getContactMessages(params),
  });
};

export const useUpdateContactReadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      isRead,
    }: {
      messageId: string;
      isRead: boolean;
    }) => updateContactReadStatus(messageId, isRead),
    // Optimistic update for immediate UI feedback
    onMutate: async ({ messageId, isRead }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["contact-messages"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData<ContactMessagesResponse>({
        queryKey: ["contact-messages"],
      });

      // Optimistically update all matching queries
      queryClient.setQueriesData<ContactMessagesResponse>(
        { queryKey: ["contact-messages"] },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.map((msg) =>
              msg._id === messageId ? { ...msg, is_read: isRead } : msg,
            ),
          };
        },
      );

      // Return context with previous data for rollback
      return { previousData };
    },
    // If mutation fails, rollback to previous data
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Always refetch after error or success to ensure sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-messages"] });
    },
  });
};

export const useMarkContactAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => markContactAsUnread(messageId),
    // Optimistic update for immediate UI feedback
    onMutate: async (messageId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["contact-messages"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData<ContactMessagesResponse>({
        queryKey: ["contact-messages"],
      });

      // Optimistically update all matching queries
      queryClient.setQueriesData<ContactMessagesResponse>(
        { queryKey: ["contact-messages"] },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.map((msg) =>
              msg._id === messageId ? { ...msg, is_read: false } : msg,
            ),
          };
        },
      );

      // Return context with previous data for rollback
      return { previousData };
    },
    // If mutation fails, rollback to previous data
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Always refetch after error or success to ensure sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-messages"] });
    },
  });
};
